import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Event, EventType } from '../types';
import { format, isValid, parseISO } from 'date-fns';

export function useEvents(selectedDate?: Date) {
  const [events, setEvents] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState(Date.now());

  // Force a refresh when needed
  const refreshEvents = () => {
    setLastRefresh(Date.now());
  };

  useEffect(() => {
    let isMounted = true;
    
    async function loadEvents() {
      try {
        setIsLoading(true);
        setError(null);

        let query = supabase.from('events').select('*');
        
        if (selectedDate && isValid(selectedDate)) {
          const dateStr = format(selectedDate, 'yyyy-MM-dd');
          query = query.eq('date', dateStr);
        }

        const { data, error: fetchError } = await query.order('date', { ascending: true });

        if (fetchError) throw fetchError;

        if (isMounted) {
          // Ensure event_type is set for all events (default to 'convention')
          const eventsWithType = (data || []).map(event => ({
            ...event,
            event_type: event.event_type || 'convention'
          }));
          setEvents(eventsWithType);
        }
      } catch (error: any) {
        console.error('Error loading events:', error);
        if (isMounted) {
          setError(error.message);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadEvents();

    // Subscribe to changes
    const channel = supabase
      .channel('events_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'events'
        },
        (payload) => {
          if (isMounted) {
            console.log('Event change detected:', payload);
            // Immediately refresh data
            loadEvents();
          }
        }
      )
      .subscribe();

    return () => {
      isMounted = false;
      channel.unsubscribe();
    };
  }, [selectedDate, lastRefresh]);

  return { events, isLoading, error, refreshEvents };
}